import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import { Column } from "../../../DataTable/DataTable";
import {
  DomainRuleRow,
  MasterOption,
  MasterRow,
  ProductCatalogService,
} from "../../../../services/api/productCatalog.service";
import { FilterSelect, MasterPanel, StatusPill } from "./MasterPanel";

type InnerTab = "domain" | "regulatory" | "primaryRule" | "subcategoryRule";

const INNER_TABS: { id: InnerTab; label: string }[] = [
  { id: "domain", label: "Domain" },
  { id: "regulatory", label: "Regulatory Domain" },
  { id: "primaryRule", label: "Primary Category Mapping" },
  { id: "subcategoryRule", label: "Sub Category Mapping" },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const toBool = (value: string): boolean | undefined =>
  value === "" ? undefined : value === "true";

const asSelectOptions = (options: MasterOption[]) =>
  options.map((option) => ({ value: option.id, label: option.name }));

/**
 * Domains, regulatory domains, and the rules that bind them to the category
 * tree.
 *
 * A rule says "this primary category (optionally narrowed to one sub category)
 * may be sold under this domain and regulatory domain". The product form reads
 * these rules to auto-fill a product's domain once its category is chosen, so
 * a category with no rule leaves the product's domain blank.
 */
export function DomainMappingTab() {
  const [innerTab, setInnerTab] = useState<InnerTab>("domain");
  const [activeFilter, setActiveFilter] = useState("");

  const [primaryOptions, setPrimaryOptions] = useState<MasterOption[]>([]);
  const [domainOptions, setDomainOptions] = useState<MasterOption[]>([]);
  const [regulatoryOptions, setRegulatoryOptions] = useState<MasterOption[]>([]);

  // Rule-tab filters.
  const [filterPrimary, setFilterPrimary] = useState("");
  const [filterDomain, setFilterDomain] = useState("");

  /**
   * Sub categories reachable from the primary category chosen inside the rule
   * dialog. Fetched through the hierarchy cascade so a rule can only be written
   * for a pair that actually exists in the tree.
   */
  const [dialogPrimaryId, setDialogPrimaryId] = useState("");
  const [cascadeSubcategories, setCascadeSubcategories] = useState<
    MasterOption[]
  >([]);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      const [primary, domain, regulatory] = await Promise.all([
        // No classificationType filter: domain rules span both trees.
        ProductCatalogService.getPrimaryCategoryOptions({ isActive: true }),
        ProductCatalogService.getDomainOptions({ isActive: true }),
        ProductCatalogService.getRegulatoryDomainOptions({ isActive: true }),
      ]);
      if (cancelled) return;
      setPrimaryOptions(primary?.success ? primary.data || [] : []);
      setDomainOptions(domain?.success ? domain.data || [] : []);
      setRegulatoryOptions(regulatory?.success ? regulatory.data || [] : []);
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    let cancelled = false;
    if (!dialogPrimaryId) {
      setCascadeSubcategories([]);
      return;
    }
    const loadCascade = async () => {
      const res =
        await ProductCatalogService.getSubcategoriesForPrimaryCategory(
          dialogPrimaryId
        );
      if (!cancelled) {
        setCascadeSubcategories(res?.success ? res.data || [] : []);
      }
    };
    loadCascade();
    return () => {
      cancelled = true;
    };
  }, [dialogPrimaryId]);

  const deleteRule = async (
    level: "primary" | "subcategory",
    id: string,
    onDone: () => void
  ) => {
    const res =
      level === "primary"
        ? await ProductCatalogService.deletePrimaryCategoryDomainRule(id)
        : await ProductCatalogService.deleteSubcategoryDomainRule(id);
    if (res?.success) {
      toast.success(res.message || "Rule deleted");
      onDone();
    } else {
      toast.error(res?.message || "Failed to delete rule");
    }
  };

  const domainColumns: Column<MasterRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Name",
        width: "w-[340px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">{row.name}</span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
      {
        key: "createdAt",
        title: "Created",
        width: "w-[140px]",
        render: (row) => (
          <span className="text-sm text-gray-500">
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}
          </span>
        ),
      },
    ],
    []
  );

  const ruleColumns = (level: "primary" | "subcategory"): Column<DomainRuleRow>[] => [
    {
      key: "primaryCategoryName",
      title: "Primary Category",
      width: "w-[200px]",
      render: (row) => (
        <span className="text-sm font-medium text-gray-800">
          {row.primaryCategoryName || row.primaryCategoryId}
        </span>
      ),
    },
    ...(level === "subcategory"
      ? [
          {
            key: "subcategoryName",
            title: "Sub Category",
            width: "w-[180px]",
            render: (row: DomainRuleRow) => (
              <span className="text-sm text-gray-700">
                {row.subcategoryName || row.subcategoryId}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "domainName",
      title: "Domain",
      width: "w-[180px]",
      render: (row) => (
        <span className="text-sm text-gray-700">
          {row.domainName || row.domainId}
        </span>
      ),
    },
    {
      key: "regulatoryDomainName",
      title: "Regulatory Domain",
      width: "w-[180px]",
      render: (row) => (
        <span className="text-sm text-gray-700">
          {row.regulatoryDomainName || row.regulatoryDomainId}
        </span>
      ),
    },
    {
      key: "isAllowed",
      title: "Allowed",
      width: "w-[110px]",
      render: (row) => (
        <StatusPill
          value={row.isAllowed}
          trueLabel="Allowed"
          falseLabel="Blocked"
        />
      ),
    },
    {
      key: "__delete",
      title: "Delete",
      width: "w-[90px]",
      render: (row) => (
        <button
          type="button"
          title="Delete rule"
          onClick={() =>
            deleteRule(level, String(row.id), () =>
              setReloadToken((token) => token + 1)
            )
          }
          className="text-red-500 hover:text-red-700"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const fetchDomains = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getDomains({
        page,
        pageSize,
        search,
        isActive: toBool(activeFilter),
      }),
    [activeFilter]
  );

  const fetchRegulatoryDomains = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getRegulatoryDomains({
        page,
        pageSize,
        search,
        isActive: toBool(activeFilter),
      }),
    [activeFilter]
  );

  const fetchPrimaryRules = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) =>
      ProductCatalogService.getPrimaryCategoryDomainRules({
        page,
        pageSize,
        primaryCategoryId: filterPrimary || undefined,
        domainId: filterDomain || undefined,
      }),
    [filterPrimary, filterDomain]
  );

  const fetchSubcategoryRules = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) =>
      ProductCatalogService.getSubcategoryDomainRules({
        page,
        pageSize,
        primaryCategoryId: filterPrimary || undefined,
        domainId: filterDomain || undefined,
      }),
    [filterPrimary, filterDomain]
  );

  const statusFilterControl = (
    <FilterSelect
      label="Status"
      value={activeFilter}
      onChange={setActiveFilter}
      options={ACTIVE_FILTER_OPTIONS}
      width="w-32"
    />
  );

  const ruleFilterControls = (
    <>
      <FilterSelect
        label="Primary Category"
        value={filterPrimary}
        onChange={setFilterPrimary}
        options={[{ value: "", label: "All" }, ...asSelectOptions(primaryOptions)]}
      />
      <FilterSelect
        label="Domain"
        value={filterDomain}
        onChange={setFilterDomain}
        options={[{ value: "", label: "All" }, ...asSelectOptions(domainOptions)]}
      />
    </>
  );

  return (
    <div className="space-y-5">
      <nav className="flex flex-wrap gap-2">
        {INNER_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setInnerTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
              innerTab === tab.id
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        {innerTab === "domain" && (
          <MasterPanel<MasterRow>
            title="Domain"
            description="The business domain a product is sold under, e.g. Pharmacy or Wellness."
            addLabel="Add Domain"
            columns={domainColumns}
            searchPlaceholder="Search domains..."
            filters={statusFilterControl}
            refreshToken={`${activeFilter}|${reloadToken}`}
            fetchRows={fetchDomains}
            fields={[
              {
                name: "name",
                label: "Name",
                type: "text",
                required: true,
                createOnly: true,
                placeholder: "e.g. Pharmacy",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ isActive: row.isActive })}
            onCreate={async (values) => {
              const res = await ProductCatalogService.createDomain({
                name: values.name,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (row, values) => {
              const res = await ProductCatalogService.updateDomain(
                String(row.id),
                { isActive: values.isActive }
              );
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}

        {innerTab === "regulatory" && (
          <MasterPanel<MasterRow>
            title="Regulatory Domain"
            description="The regulatory regime that governs the product, e.g. Drug, Cosmetic or Food."
            addLabel="Add Regulatory Domain"
            columns={domainColumns}
            searchPlaceholder="Search regulatory domains..."
            filters={statusFilterControl}
            refreshToken={`${activeFilter}|${reloadToken}`}
            fetchRows={fetchRegulatoryDomains}
            fields={[
              {
                name: "name",
                label: "Name",
                type: "text",
                required: true,
                createOnly: true,
                placeholder: "e.g. Drug",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ isActive: row.isActive })}
            onCreate={async (values) => {
              const res = await ProductCatalogService.createRegulatoryDomain({
                name: values.name,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (row, values) => {
              const res = await ProductCatalogService.updateRegulatoryDomain(
                String(row.id),
                { isActive: values.isActive }
              );
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}

        {innerTab === "primaryRule" && (
          <MasterPanel<DomainRuleRow>
            title="Primary Category Mapping"
            description="Maps a primary category to a domain and regulatory domain. Applies to every sub category unless a sub category rule overrides it."
            addLabel="Add Mapping"
            columns={ruleColumns("primary")}
            searchable={false}
            filters={ruleFilterControls}
            refreshToken={`primary|${filterPrimary}|${filterDomain}|${reloadToken}`}
            fetchRows={fetchPrimaryRules}
            fields={[
              {
                name: "primaryCategoryId",
                label: "Primary Category",
                type: "select",
                required: true,
                options: asSelectOptions(primaryOptions),
              },
              {
                name: "domainId",
                label: "Domain",
                type: "select",
                required: true,
                options: asSelectOptions(domainOptions),
              },
              {
                name: "regulatoryDomainId",
                label: "Regulatory Domain",
                type: "select",
                required: true,
                options: asSelectOptions(regulatoryOptions),
              },
              {
                name: "isAllowed",
                label: "Allowed",
                type: "boolean",
                helpText:
                  "Set to No to explicitly block the combination rather than leaving it unmapped.",
              },
            ]}
            rowToValues={(row) => ({
              primaryCategoryId: row.primaryCategoryId,
              domainId: row.domainId,
              regulatoryDomainId: row.regulatoryDomainId,
              isAllowed: row.isAllowed,
            })}
            onCreate={async (values) => {
              const res =
                await ProductCatalogService.upsertPrimaryCategoryDomainRule({
                  primaryCategoryId: values.primaryCategoryId,
                  domainId: values.domainId,
                  regulatoryDomainId: values.regulatoryDomainId,
                  isAllowed: values.isAllowed,
                });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            // The endpoint is an upsert on the unique triple, so editing an
            // existing rule is the same call as creating one.
            onUpdate={async (_row, values) => {
              const res =
                await ProductCatalogService.upsertPrimaryCategoryDomainRule({
                  primaryCategoryId: values.primaryCategoryId,
                  domainId: values.domainId,
                  regulatoryDomainId: values.regulatoryDomainId,
                  isAllowed: values.isAllowed,
                });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}

        {innerTab === "subcategoryRule" && (
          <MasterPanel<DomainRuleRow>
            title="Sub Category Mapping"
            description="Narrower than the primary category rule and wins over it when both match."
            addLabel="Add Mapping"
            columns={ruleColumns("subcategory")}
            searchable={false}
            filters={ruleFilterControls}
            refreshToken={`sub|${filterPrimary}|${filterDomain}|${reloadToken}`}
            fetchRows={fetchSubcategoryRules}
            onValuesChange={(values) =>
              setDialogPrimaryId(values.primaryCategoryId || "")
            }
            fields={[
              {
                name: "primaryCategoryId",
                label: "Primary Category",
                type: "select",
                required: true,
                options: asSelectOptions(primaryOptions),
              },
              {
                name: "subcategoryId",
                label: "Sub Category",
                type: "select",
                required: true,
                options: asSelectOptions(cascadeSubcategories),
                helpText: dialogPrimaryId
                  ? "Only sub categories mapped under the chosen primary category are listed."
                  : "Choose a primary category first.",
              },
              {
                name: "domainId",
                label: "Domain",
                type: "select",
                required: true,
                options: asSelectOptions(domainOptions),
              },
              {
                name: "regulatoryDomainId",
                label: "Regulatory Domain",
                type: "select",
                required: true,
                options: asSelectOptions(regulatoryOptions),
              },
              { name: "isAllowed", label: "Allowed", type: "boolean" },
            ]}
            rowToValues={(row) => ({
              primaryCategoryId: row.primaryCategoryId,
              subcategoryId: row.subcategoryId,
              domainId: row.domainId,
              regulatoryDomainId: row.regulatoryDomainId,
              isAllowed: row.isAllowed,
            })}
            onCreate={async (values) => {
              const res =
                await ProductCatalogService.upsertSubcategoryDomainRule({
                  primaryCategoryId: values.primaryCategoryId,
                  subcategoryId: values.subcategoryId,
                  domainId: values.domainId,
                  regulatoryDomainId: values.regulatoryDomainId,
                  isAllowed: values.isAllowed,
                });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (_row, values) => {
              const res =
                await ProductCatalogService.upsertSubcategoryDomainRule({
                  primaryCategoryId: values.primaryCategoryId,
                  subcategoryId: values.subcategoryId,
                  domainId: values.domainId,
                  regulatoryDomainId: values.regulatoryDomainId,
                  isAllowed: values.isAllowed,
                });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}
      </div>
    </div>
  );
}
